import { FC, useCallback, useMemo } from 'react'
import { FilterFloatingPanePreset } from 'components/features/pageFilters/FilterFloatingPanePreset/FilterFloatingPanePreset'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { linkedMixedAccountsGuard } from 'guards/linkageGuards'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { FilterByAccountForm } from 'components/features/pageFilters/FilterByAccountForm/FilterByAccountForm'
import { useForm, FormProvider } from 'react-hook-form'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { SetFilterByAccountsAction } from 'components/features/pageFilters/actions'
import styles from 'components/features/pageFilters/FilterByAccountPane/FilterByAccountPane.styl'
import { Kebab } from 'components/features/pfm/Kebab/Kebab'
import { PAGE_DEFINITIONS_MAP } from 'constants/pages'
import { AccountsFilteredPage } from 'interfaces/pages'
import { accountsEnabledStateUidsMapSelector } from 'components/features/pageFilters/selectors'
import { defaultMemoize } from 'reselect'
import Button from 'components/features/pfm/Button/Button'
import { CANCEL_BUTTON_PROPS } from 'components/features/pfm/Button/FloatingPaneButtons'
import { IS_MOBILE } from 'helpers/userAgentHelpers'

const SUB_TITLE = 'Select which Accounts to show'
const getPaneTitle = defaultMemoize((page: AccountsFilteredPage) => {
  const { title } = PAGE_DEFINITIONS_MAP[page]
  if (page === 'dashboard') {
    return `Filter ${ title }`
  }

  return `Filter Your ${ title }`
})

const EMPTY_OBJ = {}
type FilterByAccountPaneProps = {
  page: AccountsFilteredPage
  onClose: () => void
}
export const FilterByAccountPane: FC<FilterByAccountPaneProps> = (props) => {
  const { page, onClose } = props
  const isLoaded = useSimpleGuard(linkedMixedAccountsGuard, EMPTY_OBJ, 'FilterByAccountPane')

  const handleAccept = useCallback(() => { /* do nothing */ }, [])

  if (isLoaded) {
    return (
      <FilterByAccountPaneInner page={ page } onClose={ onClose } />
    )
  }

  return (
    <FilterFloatingPanePreset
      title={ getPaneTitle(page) }
      subTitle={ SUB_TITLE }
      compact
      actionName='Accept'
      onAction={ handleAccept }
      actionDisabled
    >
      <LoadingOverlay />
    </FilterFloatingPanePreset>
  )
}

type FilterByAccountPaneInnerProps = {
  page: AccountsFilteredPage
  onClose: () => void
}
const FilterByAccountPaneInner: FC<FilterByAccountPaneInnerProps> = (props) => {
  const { page, onClose } = props

  const accountsEnabledState = useStoreSelector(accountsEnabledStateUidsMapSelector)
  const context = useForm<Record<string, boolean>>({
    defaultValues: accountsEnabledState,
  })
  const { handleSubmit, getValues, setValue, formState, watch } = context

  const formValues = watch()
  const isValid = useMemo(() => {
    return Object.keys(formValues).filter((key) => formValues[key]).length > 0
  }, [formValues])

  const dispatch = useStoreDispatch()
  const handleAccept = useCallback(() => {
    if (!formState.isDirty) {
      onClose()
      return
    }

    handleSubmit((values) => {
      dispatch(new SetFilterByAccountsAction({
        uids: values,
      }))

      onClose()
    })()
  }, [dispatch, formState.isDirty, handleSubmit, onClose])

  const handleSelectAll = useCallback(() => {
    const values = getValues()
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        setValue(key, true, { shouldDirty: true })
      }
    })
  }, [getValues, setValue])

  const handleClearSelection = useCallback(() => {
    const values = getValues()
    Object.keys(values).forEach((key) => {
      if (!!values[key]) {
        setValue(key, false, { shouldDirty: true })
      }
    })
  }, [getValues, setValue])

  const hasDisabled = useMemo(() => {
    return Object.keys(formValues).filter((key) => !formValues[key]).length > 0
  }, [formValues])

  const formChild = useMemo(() => {

    return (
      <FormProvider { ...context }>
        <FilterByAccountForm
          onSelectAll={ hasDisabled ? handleSelectAll : undefined }
          onClearSelection={ !hasDisabled ? handleClearSelection : undefined }
        />
      </FormProvider>
    )
  }, [context, handleClearSelection, handleSelectAll, hasDisabled])

  return (
    <FilterFloatingPanePreset
      title={ getPaneTitle(page) }
      subTitle={ SUB_TITLE }
      compact
      actionName='Apply filters'
      actionDisabled={ !isValid }
      onAction={ handleAccept }
      onClose={ onClose }
    >
      <div className={ styles.note } data-qa-marker='filtering-pane-note'>
        <div className={ styles.icon }>
          <Kebab />
        </div>
        <div className={ styles.text }>
          Comparisons are more helpful when when all accounts are selected.
          { ' ' }
          { hasDisabled && (
            <a onClick={ handleSelectAll } data-qa-marker='select-all'>Select All</a>
          )}
        </div>
      </div>
      { formChild }
      { IS_MOBILE && <Button { ...CANCEL_BUTTON_PROPS } className={ styles.cancelButton } size='tiny' onClick={ onClose } /> }
    </FilterFloatingPanePreset>
  )
}


